import { useModal } from "hooks/useModal";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import type { Location } from "history";

export const useUnsavedChanges = ({ isDirty }: { isDirty: boolean }) => {
  const history = useHistory();
  const unsavedModal = useModal();
  const unblockRef = useRef<(() => void) | null>(null);
  const [blockedLocation, setBlockedLocation] = useState<Location | null>(null);

  useEffect(() => {
    if (!isDirty) return;

    unblockRef.current = history.block((location) => {
      setBlockedLocation(location as Location);
      unsavedModal.open();
      return false;
    });

    return () => unblockRef.current?.();
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return false;
    };

    window.addEventListener("beforeunload", handler, { capture: true });
    return () =>
      window.removeEventListener("beforeunload", handler, { capture: true });
  }, [isDirty]);

  const handleExit = () => {
    unblockRef.current?.();
    unblockRef.current = null;
    unsavedModal.close();
    if (blockedLocation) {
      history.push(blockedLocation.pathname + blockedLocation.search);
    }
  };

  const handleSave = async () => {
    unblockRef.current?.();
    unblockRef.current = null;
    unsavedModal.close();
  };

  const handleClose = () => {
    unsavedModal.close();
  };

  return {
    isUnsavedModalOpen: unsavedModal.isOpen,
    handleClose,
    handleExit,
    handleSave,
  };
};
